import * as Sentry from '@sentry/react'
import { authSelectors } from 'ducks/auth'
import { getAddComponentFromSelectorContext } from 'amplitude/helpers/componentSelector'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { handleAddComponent } from 'projectSections/sections/design/util/handleAddComponent'
import {
  shouldBlockManualSolveraComponentSelection,
  SOLVERA_MANUAL_SELECTION_BLOCKED_MESSAGE,
} from 'projectSections/sections/design/systems/solveraComponentCode'
import {
  getFirstLockedStudioTarget,
  getStudioComponentLockedMessage,
  shouldBlockLockedStudioTargetReplacement,
} from 'projectSections/sections/design/systems/studioComponentLock'
import { useCallback } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { OSComponentType } from 'types/components'
import { StudioComponentItem } from 'types/global'
import { ComponentTypes } from 'types/selectComponent'
import { ActionType } from 'types/tracking'
import { plural } from '../../elements/hardwareSelector/HardwareSelector'
import { updateSystemHardware } from '../../elements/hardwareSelector/updateSystemHardware'
import useLoadComponentSpecs from './useLoadComponentSpecs'

var restClientInstance = restClient(window.API_ROOT + '/api')

const reportAuthoredByUndefined = (authoredBy: string | undefined, componentType: ComponentTypes) => {
  if (authoredBy === undefined) {
    Sentry.captureMessage('addComponentToSystem: authored_by is undefined', {
      level: 'warning',
      extra: { componentType },
    })
  }
}

/**
 * Returns available component specifications based on the type.
 */
const getActiveComponentsData = (componentType: ComponentTypes, data?: OSComponentType[]) => {
  if (data) return data

  switch (componentType) {
    case 'module':
      return window.AccountHelper.getComponentModuleSpecsAvailable()
    case 'inverter':
      return window.AccountHelper.getComponentInverterSpecsAvailable()
    case 'battery':
      return window.AccountHelper.getComponentBatterySpecsAvailable()
    case 'other':
      return window.AccountHelper.getComponentOtherSpecsAvailable()
    default:
      throw new Error(`Unknown component type: ${componentType}`)
  }
}

/**
 * Attempts to find an existing activation from current or loaded data.
 */
const findMatchingActivation = (
  componentType: ComponentTypes,
  activeData: OSComponentType[],
  componentActivationId?: number | string,
  componentId?: number,
  componentCode?: string
) => {
  let matchingActivation

  // Try to find by activation id first
  if (componentActivationId) {
    matchingActivation = activeData.find((item) => item.id === componentActivationId)
    if (matchingActivation && componentId === undefined) {
      switch (componentType) {
        case 'module':
          componentId = matchingActivation['module_id']
          break
        case 'inverter':
          componentId = matchingActivation['inverter_id']
          break
        case 'battery':
          componentId = matchingActivation['battery_id']
          break
        case 'other':
          componentId = matchingActivation['other_id']
          break
      }
    }
  }

  // If not found, check loaded data (this handles archived or previously activated components)
  // TODO: use resourceService instead
  if (!matchingActivation) {
    const loadedDataMap: Record<ComponentTypes, OSComponentType[]> = {
      module: window.AccountHelper.loadedData.componentModuleSpecs,
      inverter: window.AccountHelper.loadedData.componentInverterSpecs,
      battery: window.AccountHelper.loadedData.componentBatterySpecs,
      other: window.AccountHelper.loadedData.componentOtherSpecs,
      custom: [],
    }
    const loadedData = loadedDataMap[componentType]
    if (loadedData) {
      const idField = componentType + '_id'
      matchingActivation =
        loadedData.find((item) => item[idField] === componentId) ||
        loadedData.find((item) => item['code'] === componentCode)
    }
  }

  return matchingActivation
}

const addOrUpdateToSystem = (
  componentType: string,
  activationId: number,
  slotKey?: string,
  targetUuids?: string[],
  quantity?: number,
  autoAdded?: boolean,
  authoredBy?: string,
  onComplete?: (items: StudioComponentItem[]) => void
) => {
  // Abort if quantity is explicitly 0 to prevent adding unwanted components
  if (quantity === 0) {
    console.log(
      `[addOrUpdateToSystem] Aborting - quantity is 0 for componentType ${componentType}, activationId ${activationId}`
    )
    onComplete?.([])
    return
  }

  var params: Record<string, string | number | boolean> = {}
  params.autoAdded = autoAdded ?? false
  if (authoredBy) {
    params.authored_by = authoredBy
  }
  let addedObjects: StudioComponentItem[] = []
  // When changing an existing component, use updateSystemHardware
  if (targetUuids) {
    addedObjects = updateSystemHardware(componentType, activationId, targetUuids, params)
  } else {
    // We are selecting a component to add to the system, so we are not replacing an old component
    params[componentType + 'Id'] = activationId

    if (slotKey) {
      params.slotKey = slotKey
    }
    if (quantity) {
      params.quantity = quantity
    }

    // @TODO: It's not safe to use selectedSystem.uuid
    // We should instead pass the systemUuid into the redux state for the dialog
    var systemUuid = window.editor.selectedSystem.uuid
    let addedObject = handleAddComponent(componentType, systemUuid, params)
    if (addedObject) addedObjects = [addedObject]
  }
  onComplete?.(addedObjects)
}

type BaseArgs = {
  componentType: ComponentTypes
  activeComponentsData?: OSComponentType[]
  targetUuids?: string[]
  slotKey?: string
  quantity?: number // currently only works for 'other' type
  autoAdded?: boolean
  authored_by?: string
  closeDialog?: () => void
  onSelect?: (items: StudioComponentItem[]) => void
  selectedComponentCode?: string
  trackAddSuccess?: boolean
}
type ArgsActivated = BaseArgs & {
  componentActivationId: number | string
  componentId?: number
  componentCode?: string
}
type ArgsDatabase = BaseArgs & {
  componentActivationId?: number | string
  componentId: number
  componentCode?: string
}

type ArgsCode = BaseArgs & {
  componentCode: string
  componentActivationId?: number | string
  componentId?: number
}

export const useAddComponentToSystem = () => {
  const notify = useNotify()
  const translate = useTranslate()
  const orgId = useSelector(authSelectors.getOrgId) as number
  const loadComponentSpecs = useLoadComponentSpecs()
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'component_selector_add',
    eventName: 'add component from selector',
    description: 'Component successfully added to system',
  })

  return useCallback(
    async ({
      componentType,
      activeComponentsData: providedData,
      targetUuids,
      slotKey,
      closeDialog,
      onSelect,
      componentActivationId,
      componentId: initialComponentId,
      quantity,
      componentCode,
      autoAdded = false,
      authored_by,
      selectedComponentCode,
      trackAddSuccess = false,
    }: ArgsActivated | ArgsDatabase | ArgsCode) => {
      const activeComponentsData = getActiveComponentsData(componentType, providedData)

      let componentId = initialComponentId
      let matchingActivation = findMatchingActivation(
        componentType,
        activeComponentsData,
        componentActivationId,
        componentId,
        componentCode
      )

      if (!matchingActivation) {
        await loadComponentSpecs()
        matchingActivation = findMatchingActivation(
          componentType,
          getActiveComponentsData(componentType, providedData),
          componentActivationId,
          componentId,
          componentCode
        )
      }

      const resolvedComponentCode =
        selectedComponentCode ||
        componentCode ||
        (matchingActivation?.code != null ? String(matchingActivation.code) : undefined) ||
        (componentType === 'other' && componentId != null
          ? window.AccountHelper.getOtherById(componentId)?.code
          : undefined)

      if (
        shouldBlockLockedStudioTargetReplacement({
          targetUuids,
          autoAdded,
          authoredBy: authored_by,
        })
      ) {
        const lockedTarget = getFirstLockedStudioTarget(targetUuids)
        notify(translate(getStudioComponentLockedMessage(lockedTarget || {})), 'warning')
        return Promise.reject('Locked studio component replacement blocked')
      }

      if (
        shouldBlockManualSolveraComponentSelection({
          componentType,
          code: resolvedComponentCode,
          autoAdded,
          authoredBy: authored_by,
        })
      ) {
        notify(translate(SOLVERA_MANUAL_SELECTION_BLOCKED_MESSAGE), 'warning')
        return Promise.reject('Solvera component manual selection blocked')
      }

      const trackSuccessfulAdd = (addedObjects: StudioComponentItem[]) => {
        if (!addedObjects.some(Boolean)) {
          return
        }
        const component_code =
          selectedComponentCode ||
          (matchingActivation?.code != null ? String(matchingActivation.code) : undefined) ||
          (componentCode != null ? String(componentCode) : undefined)
        if (!component_code) {
          return
        }
        trackUserInteraction(
          { type: ActionType.CLICK },
          {
            ...getAddComponentFromSelectorContext({
              componentTypes: [componentType],
              componentCode: component_code,
            }),
            action: 'component_added',
          }
        )
      }

      if (matchingActivation) {
        reportAuthoredByUndefined(authored_by, componentType)
        //signals and controllers are disabled in dialog
        //enable signals and controllers before choosing new component type
        window.editor?.interactive(true)
        addOrUpdateToSystem(
          componentType,
          matchingActivation.id as number,
          slotKey,
          targetUuids,
          quantity,
          autoAdded,
          authored_by,
          (addedObjects) => {
            if (trackAddSuccess) {
              trackSuccessfulAdd(addedObjects)
            }
            onSelect?.(addedObjects)
          }
        )
        closeDialog?.()
        return Promise.resolve('Component assigned to system')
      }

      const activationStrategy = (() => {
        if (initialComponentId) {
          return {
            url: `orgs/${orgId}/component_${componentType}_activations/`,
            data: { [`${componentType}`]: '/api/component_' + plural(componentType) + '/' + initialComponentId + '/' },
            getActivationId: (response: any) => response.data.id,
          }
        }
        if (componentCode) {
          return {
            url: `orgs/${orgId}/activate_components/`,
            data: { codes: [componentCode] },
            getActivationId: (response: any) => response.data.activation_details[componentCode].id,
          }
        }
        throw new Error('Either componentId or componentCode must be provided')
      })()

      let activatePromise = restClientInstance('CUSTOM_POST', 'custom', {
        url: activationStrategy.url,
        data: activationStrategy.data,
      })

      return activatePromise.then(
        async (response: any) => {
          notify('Component activated on account & assigned to system.')
          const onComplete = (addedObjects: StudioComponentItem[]) => {
            if (trackAddSuccess) {
              trackSuccessfulAdd(addedObjects)
            }
            window.editor?.interactive(true)
            onSelect?.(addedObjects)
            closeDialog?.()
          }

          const activationId = activationStrategy.getActivationId(response)

          // const componentSpec =
          //   response.data.component_spec || response.data.activation_details?.[componentCode || '']?.component_spec

          // injectComponentSpec doesn't work with latest resourceService, need fix before turn it on

          // if (componentSpec) {
          //   injectComponentSpec(componentType, componentSpec)
          // } else {
          //   await loadComponentSpecs()
          // }

          await loadComponentSpecs()

          reportAuthoredByUndefined(authored_by, componentType)

          addOrUpdateToSystem(
            componentType,
            activationId,
            slotKey,
            targetUuids,
            quantity,
            autoAdded,
            authored_by,
            onComplete
          )
        },
        () => {
          notify(translate('Error activating component on account.'), 'warning')
          return Promise.reject('Error activating component on account.')
        }
      )
    },
    []
  )
}
